import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, FileDown, Search, ShieldCheck } from "lucide-react";
import { useSort, SortTh } from "@/components/SortTh";
import { useNavigate } from "react-router-dom";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { StatusBadge } from "@/pages/OrderPage";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ManualOrder from "@/pages/admin/ManualOrder";

const STATUSES = ["pending", "paid", "processing", "shipped", "ready_for_pickup", "completed", "cancelled"];
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export function printLabel(o, config) {
  const sh = o.shipping;
  const items = o.items.map((i) => `<tr><td>${esc(i.name)}</td><td>${esc(i.type)}</td><td>${esc(Object.values(i.variant || {}).join(" · "))}</td><td>${i.qty}</td></tr>`).join("");
  const to = sh.method === "courier" ? `${esc(sh.address)}<br>${esc(sh.city)} ${esc(sh.postal_code)}, ${esc(sh.country)}` : sh.method === "pickup" ? "Ambil di Toko" : "Digital / online";
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) { toast.error("Allow pop-ups to print"); return; }
  w.document.write(`<html><head><title>Label ${esc(o.order_id)}</title><style>body{font-family:Georgia,serif;padding:24px;color:#1A1412}h1{font-size:20px;margin:0 0 12px}.box{border:2px solid #3B220C;padding:14px;margin-bottom:12px}.k{font-size:11px;letter-spacing:.15em;color:#8C4F27}table{width:100%;border-collapse:collapse;font-size:13px}td,th{border:1px solid #ccc;padding:6px;text-align:left}</style></head><body>
    <h1>CRAFTENDEMIC · ${esc(o.order_id)}</h1>
    <div class="box"><div class="k">PENERIMA / SHIP TO</div><b>${esc(o.customer.name)}</b><br>${to}<br>Telp: ${esc(o.customer.phone)}</div>
    <div class="box"><div class="k">PENGIRIM / FROM</div><b>Craftendemic</b><br>${esc(config.store_address)}<br>Telp: ${esc(config.whatsapp_number)}</div>
    <div class="box"><div class="k">KURIR & RESI</div>${sh.method === "courier" ? `${esc(sh.courier_name)} ${esc(sh.service_name)}` : esc(sh.method)} · Resi: <b>${esc(o.tracking_number || "-")}</b></div>
    <table><tr><th>Produk</th><th>Jenis</th><th>Varian</th><th>Qty</th></tr>${items}</table>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

function PrintButtons({ o }) {
  const { config } = useApp();
  const paid = !["pending", "cancelled"].includes(o.status);
  return (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      <button disabled={!paid} title="Print invoice" onClick={() => downloadBlob(`/orders/${o.order_id}/invoice`, `invoice-${o.order_id}.pdf`)} className="border border-[#3B220C] p-1.5 disabled:opacity-30" data-testid={`print-invoice-${o.order_id}`}><FileDown size={14} /></button>
      <button title="Print shipping label" onClick={() => printLabel(o, config)} className="border border-[#3B220C] p-1.5" data-testid={`print-label-${o.order_id}`}><Printer size={14} /></button>
    </div>
  );
}

const STATUS_ORDER = { pending: 0, paid: 1, processing: 2, shipped: 3, ready_for_pickup: 4, completed: 5, cancelled: 6 };
const ORDER_SORT = {
  order: (o) => o.order_id, date: (o) => o.created_at, customer: (o) => o.customer.name.toLowerCase(), items: (o) => o.items.reduce((s, i) => s + i.qty, 0),
  delivery: (o) => (o.shipping.method === "courier" ? o.shipping.courier_name || "" : o.shipping.method === "pickup" ? "Pickup" : "~"), total: (o) => o.total, status: (o) => STATUS_ORDER[o.status] ?? 9,
  warranty: (o) => (!canWarranty(o) ? 2 : o.warranty_serial ? 1 : 0),
};
const ORDER_COLS = [["Order", "order"], ["Date", "date"], ["Customer", "customer"], ["Items", "items"], ["Delivery", "delivery"], ["Total", "total"], ["Status", "status"], ["Warranty", "warranty"], ["Print", null]];

const createUrl = (o) => {
  const ph = o.items.find((i) => i.type === "physical");
  return `/admin/warranty?order=${o.order_id}&product=${ph?.product_id || ""}&variant=${encodeURIComponent(JSON.stringify(ph?.variant || {}))}&made=${(o.paid_at || o.created_at).slice(0, 10)}`;
};
const canWarranty = (o) => !["pending", "cancelled"].includes(o.status) && o.items.some((i) => i.type === "physical");

function WarrantyIcon({ o }) {
  const { user } = useApp();
  const nav = useNavigate();
  if (!canWarranty(o)) return null;
  const linked = !!o.warranty_serial;
  const click = (e) => {
    e.stopPropagation();
    if (linked) { nav(`/admin/warranty?serial=${o.warranty_serial}`); return; }
    if (user.role !== "superadmin") { toast.error("Only the superadmin can create a warranty card"); return; }
    nav(createUrl(o));
  };
  return (
    <button onClick={click} title={linked ? `Warranty ${o.warranty_serial} — view / edit` : "Create warranty card"} data-testid={`order-warranty-icon-${o.order_id}`} data-linked={linked}
      className={`w-8 h-8 rounded-full grid place-items-center text-white transition-transform hover:scale-110 ${linked ? "bg-[#9E9E9E]" : "bg-[#2E7D32]"}`}><ShieldCheck size={16} /></button>
  );
}

function OrderWarranty({ o }) {
  const { user } = useApp();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [serial, setSerial] = useState("");
  const load = () => api.get("/admin/warranties", { params: { order_id: o.order_id } }).then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const link = async () => { try { await api.post(`/admin/warranties/${serial.trim().toUpperCase()}/link`, { order_id: o.order_id }); setSerial(""); toast.success("Warranty linked to this order"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!canWarranty(o) || !rows) return null;
  const linked = rows.length > 0;
  return (
    <div className="border border-[#E6DFD5] p-4 space-y-3" data-testid="order-warranty">
      <p className="flex items-center gap-2 font-medium"><ShieldCheck size={16} />Authentication & Warranty</p>
      {rows.map((w) => <button key={w.serial} onClick={() => nav(`/admin/warranty?serial=${w.serial}`)} className="text-xs underline text-left" data-testid={`order-warranty-link-${w.serial}`}><span className="font-mono">{w.serial}</span> · {w.title} · {w.status === "valid" ? "Active" : "Expired"} — view / edit</button>)}
      {!linked && <div className="flex flex-wrap gap-2">
        {user.role === "superadmin" && <Btn onClick={() => nav(createUrl(o))} data-testid="order-create-warranty-btn">Create warranty card</Btn>}
        <input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Link existing serial CEW-..." className={`${inputCls} !w-56`} data-testid="order-link-warranty-input" />
        <button disabled={!serial.trim()} onClick={link} className="border border-[#3B220C] px-3 text-sm disabled:opacity-40" data-testid="order-link-warranty-btn">Link</button>
      </div>}
    </div>
  );
}

function OrderDialog({ o, onSaved }) {
  const { fmt } = useApp();
  const [status, setStatus] = useState(o.status);
  const [tracking, setTracking] = useState(o.tracking_number || "");
  const save = async () => {
    try {
      await api.patch(`/admin/orders/${o.order_id}`, { status: status === o.status ? null : status, tracking_number: tracking });
      toast.success("Order updated — customer notified by email & WhatsApp");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sh = o.shipping;
  return (
    <div className="space-y-5 text-sm" data-testid="order-dialog">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><p className="text-xs text-[#6E6259]">Customer</p><p>{o.customer.name}</p><p>{o.customer.email}</p><p>{o.customer.phone}</p>{o.source === "manual" && <p className="text-xs text-[#8C4F27] mt-1" data-testid="order-manual-info">Manual order by {o.created_by}{o.note && ` — ${o.note}`}</p>}</div>
        <div><p className="text-xs text-[#6E6259]">Delivery</p>
          {sh.method === "courier" && <p>{sh.courier_name} {sh.service_name} · {fmt(sh.cost)}<br />{sh.address}, {sh.city} {sh.postal_code}, {sh.country}</p>}
          {sh.method === "pickup" && <p>Ambil di Toko (in-store pickup)</p>}
          {sh.method === "none" && <p>Digital / academy only</p>}
        </div>
      </div>
      <div className="border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {o.items.map((i, k) => <div key={k} className="p-3 flex justify-between gap-4"><span>{i.name} ×{i.qty} <span className="text-xs text-[#6E6259]">{Object.values(i.variant || {}).join(" · ")}{i.session_start && ` · ${new Date(i.session_start).toLocaleString()}`}</span></span><span>{fmt(i.price * i.qty)}</span></div>)}
        <div className="p-3 flex justify-between font-medium"><span>Total {o.discount > 0 && `(−${fmt(o.discount)} ${o.coupon_code})`} {o.member_discount > 0 && `(−${fmt(o.member_discount)} member)`}</span><span>{fmt(o.total)}</span></div>
      </div>
      <OrderWarranty o={o} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Status"><select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="order-status-select">{[o.status, ...STATUSES.filter((s) => !(s === o.status || s === "pending" || s === "paid") && (o.status !== "pending" || s === "cancelled"))].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></Field>
        <Field label="Tracking number"><input value={tracking} onChange={(e) => setTracking(e.target.value)} className={inputCls} data-testid="order-tracking-input" /></Field>
      </div>
      <div className="flex justify-between items-center gap-3">
        <PrintButtons o={o} />
        <p className="text-xs text-[#6E6259] flex-1">Adding a tracking number marks courier orders as Shipped automatically; delivery is checked hourly and the order is completed when the courier reports delivered.</p>
        <Btn onClick={save} data-testid="order-save-btn">Save & notify</Btn>
      </div>
    </div>
  );
}

export function Orders() {
  const { fmt } = useApp();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const [manual, setManual] = useState(false);
  const load = () => api.get("/admin/orders", { params: { status: filter } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  const k = q.trim().toLowerCase();
  const filtered = rows.filter((o) => !k || [o.order_id, o.customer.name, o.customer.email, o.customer.phone, o.tracking_number, ...o.items.map((i) => i.name)].some((x) => (x || "").toLowerCase().includes(k)));
  const { rows: list, sort, toggle } = useSort(filtered, ORDER_SORT);
  return (
    <div data-testid="admin-orders">
      <AdminTitle action={<div className="flex flex-wrap gap-2"><Btn onClick={() => setManual(true)} data-testid="manual-order-open-btn">+ Manual order</Btn><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orders..." className={`${inputCls} w-56`} data-testid="orders-search" /><select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} w-48`} data-testid="orders-filter"><option value="">All statuses</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>}>Orders</AdminTitle>
      <div className="bg-white border border-[#E6DFD5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#6E6259] border-b border-[#E6DFD5]"><tr>{ORDER_COLS.map(([h, key]) => <SortTh key={h} label={h} k={key} sort={sort} toggle={toggle} testid="orders" />)}</tr></thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.order_id} onClick={() => setOpen(o)} className="border-b border-[#E6DFD5] last:border-0 cursor-pointer hover:bg-[#F6F2EE]" data-testid={`admin-order-row-${o.order_id}`}>
                <td className="p-3 font-mono text-xs">{o.order_id}</td>
                <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                <td className="p-3">{o.customer.name}{!o.user_id && <span className="text-[10px] text-[#6E6259]"> · guest</span>}{o.source === "manual" && <span className="text-[10px] text-[#8C4F27]" data-testid={`order-manual-tag-${o.order_id}`}> · manual</span>}</td>
                <td className="p-3">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                <td className="p-3 text-xs">{o.shipping.method === "courier" ? o.shipping.courier_name : o.shipping.method === "pickup" ? "Pickup" : "—"}</td>
                <td className="p-3">{fmt(o.total)}</td>
                <td className="p-3"><StatusBadge s={o.status} /></td>
                <td className="p-3"><WarrantyIcon o={o} /></td>
                <td className="p-3"><PrintButtons o={o} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-6 text-sm text-[#6E6259]">No orders.</p>}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-mono text-lg">{open?.order_id}</DialogTitle>
          {open && <OrderDialog o={open} onSaved={() => { setOpen(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={manual} onOpenChange={setManual}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl font-normal">Manual order</DialogTitle>
          {manual && <ManualOrder onDone={() => { setManual(false); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Bookings() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/bookings").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const attend = async (b) => { try { await api.patch(`/admin/bookings/${b.booking_id}/attended`); toast.success("Certificate issued"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const k = q.trim().toLowerCase();
  const list = rows.filter((b) => !k || [b.product_name, b.user_name, b.user_email, b.mode, b.order_id].some((x) => (x || "").toLowerCase().includes(k)));
  return (
    <div data-testid="admin-bookings">
      <AdminTitle action={<div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6259]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings..." className={`${inputCls} w-64 pl-9`} data-testid="bookings-search" /></div>}>Workshop Bookings</AdminTitle>
      <div className="bg-white border border-[#E6DFD5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#6E6259] border-b border-[#E6DFD5]"><tr>{["Session", "Workshop", "Participant", "Seats", "Format", ""].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.booking_id} className="border-b border-[#E6DFD5] last:border-0" data-testid={`admin-booking-row-${b.booking_id}`}>
                <td className="p-3 text-xs">{new Date(b.session_start).toLocaleString()}</td>
                <td className="p-3">{b.product_name}</td>
                <td className="p-3">{b.user_name}<br /><span className="text-xs text-[#6E6259]">{b.user_email}</span></td>
                <td className="p-3">{b.seats}</td>
                <td className="p-3 font-mono text-xs">{b.mode}</td>
                <td className="p-3">{b.attended ? <span className="text-xs text-[#2E6B40]">Certified</span> : <Btn onClick={() => attend(b)} className="py-1.5 text-xs" data-testid={`mark-attended-${b.booking_id}`}>Mark attended</Btn>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-6 text-sm text-[#6E6259]">No bookings yet.</p>}
      </div>
    </div>
  );
}
